import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import Button from 'components/Button/Button';
import ButtonDropDawn from 'components/ButtonDropDawn/ButtonDropDawn';
import ContentLoader from 'components/ContentLoader/ContentLoader';
import ImportFileModal from 'components/ImportFileModal/ImportFileModal';
import Loader from 'components/Loader/Loader';
import MaterialConfigEmpty from 'components/MaterialConfigEmpty/MaterialConfigEmpty';
import Panel from 'components/Panel/Panel';
import QuestionErrorModal from 'components/QuestionErrorModal/QuestionErrorModal';
import TitleWrapper from 'components/TitleWrapper/TitleWrapper';
import { DISCLOSURE_CELLS, DISCLOSURE_GROUP_CELLS } from 'constants/dataSets';
import { MODAL_QUESTIONS, OPTIONS_QUESTIONS_MENU } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';

import CreateQuestion from './components/CreateQuestion/CreateQuestion';
import Table from './components/Table/Table';

import type IMaterialityConfigInvestorsCustomersEvidence from './interfaces/IMaterialityConfigInvestorsCustomersEvidence';

import styles from './scss/MaterialityConfigInvestorsCustomersEvidence.module.scss';

function MaterialityConfigDisclosureView({
  goBack, onSave, onDownloadExample, uploadControl, isEmptyList,
  control, isLineMark, items, onOpenModal, openModal,
  onCloseModal, hasMore, loadMore, expandedRowId, setExpandedRowId,
  questions, onSelectItem, loading, loadReq, errorLog, isSaveDisabled,
  recordId, setOpenModal, vcOptions, vcmId, questionErrorModal, questionErrorList, onQuestionGroupCreated,
  isViewOnly = false,
}: IMaterialityConfigInvestorsCustomersEvidence) {
  const { t } = useTranslation();
  return (
    <Panel className={styles.root}>
      <div className={styles.rootContainer}>
        <div className={styles.wrapper}>
          <Button
            title={t('materialityAssessment.back')}
            onClick={goBack}
            variant={BUTTON_VARIANT.ghost}
            startIcon={<ArrowLeftIcon />}
          />
          <div className={styles.contentWrapper}>
            {loadReq ? <Loader isPageLoading={loadReq} isFullLoader={false} /> : null}
            {loading && !loadReq ? (<ContentLoader title={t('materialityAssessment.loadingDisclosure')} />) : null}
            {isEmptyList && !loading && !loadReq ? (
              <MaterialConfigEmpty
                title={t('materialityAssessment.otherNonCorporateDisclosures')}
                subTitle={t('materialityAssessment.otherNonCorporateDisclosuresDescription')}
                uploadControl={uploadControl}
                onDownloadExample={onDownloadExample}
                subTitleBox={t('materialityAssessment.investorsCustomersEvidenceSubTitleBox')}
                titleBox={t('materialityAssessment.investorsCustomersEvidenceTitleBox')}
                titleDropdownButton={t('materialityAssessment.addQuestion')}
                onClickDropdownButton={isViewOnly ? undefined : onOpenModal}
                optionsDropdown={OPTIONS_QUESTIONS_MENU}
              />
            ) : null}
            {!isEmptyList && !loading && !loadReq ? (
              <div className={styles.tableWrapper}>
                <div className={styles.titleWrapper}>
                  <TitleWrapper
                    title={t('materialityAssessment.otherNonCorporateDisclosures')}
                    description={t('materialityAssessment.otherNonCorporateDisclosuresDescription')}
                  />
                  <div className={styles.buttonGroup}>
                    <Button
                      title={t('dataSetsManagement.downloadExample')}
                      startIcon={<DownloadIcon />}
                      variant="ghost"
                      onClick={onDownloadExample}
                    />
                    {!isViewOnly && (
                      <ButtonDropDawn
                        title={t('materialityAssessment.addQuestion')}
                        onClick={onOpenModal}
                        options={OPTIONS_QUESTIONS_MENU}
                      />
                    )}
                  </div>
                </div>
                <Table
                  headCells={DISCLOSURE_CELLS}
                  headCellsMain={DISCLOSURE_GROUP_CELLS}
                  control={control}
                  isLineMark={isLineMark}
                  items={items}
                  hasMore={hasMore}
                  loadMore={loadMore}
                  expandedRowId={expandedRowId}
                  setExpandedRowId={setExpandedRowId}
                  questions={questions}
                  onSelectItem={onSelectItem}
                  vcOptions={vcOptions}
                  isViewOnly={isViewOnly}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={goBack}
          />
          <Button
            title={t('common.save')}
            onClick={onSave}
            className={styles.button}
            disabled={isSaveDisabled}
          />
        </div>
        <ImportFileModal
          open={openModal === MODAL_QUESTIONS.IMPORT_FILE}
          onCloseModal={onCloseModal}
          onDownloadExample={onDownloadExample}
          uploadControl={uploadControl}
          titleBox={t('materialityAssessment.investorsCustomersEvidenceTitleBox')}
          subTitleBox={t('materialityAssessment.investorsCustomersEvidenceSubTitleBox')}
          titleWrapper={t('materialityAssessment.uploadFileWithQuestions')}
          hideSubTitleWrapper
        />
        <CreateQuestion
          open={openModal === MODAL_QUESTIONS.CREATE_NEW}
          onClose={onCloseModal}
          title={t('materialityAssessment.createNewQuestion')}
          recordId={recordId}
          setOpenModal={setOpenModal}
          setExpandedRowId={setExpandedRowId}
          vcmId={vcmId}
          onQuestionGroupCreated={onQuestionGroupCreated}
        />
        <QuestionErrorModal
          isOpen={questionErrorModal}
          onClose={onCloseModal}
          questionErrorList={questionErrorList}
          errorLog={errorLog}
          title={t('materialityAssessment.otherNonCorporateDisclosures')}
        />
      </div>
    </Panel>
  );
}

export default MaterialityConfigDisclosureView;
